import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Forward,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { catColor, catLabel } from './categories';
import './ClassificationReview.css'; // the --cat-* palette, via .cat-palette
import {
  apiMessage,
  deleteCategoryForward,
  deleteReporter,
  deleteRoute,
  getRoutingOverview,
  putCategoryForward,
  putReporter,
  putRoute,
} from '../services/serviceInboxApi';

/**
 * Task Routing — where each category's email goes, and who its ServiceTitan
 * task is assigned to.
 *
 * The data lives in the service inbox's `service_inbox` schema and is served by
 * its /api/inbox/routing endpoints (serviceInboxApi.js); this page owns none of
 * it. Three things, in two sections (redesigned 2026-10-19):
 *
 *   1. Forwarding, by category. Each category has its own forward list, which
 *      applies to every email in the category whether or not the customer was
 *      matched. Under a category, each office can name its own assignee for the
 *      ServiceTitan task and, optionally, its own forward list; an office with no
 *      list of its own inherits the category's. The office settings only come into
 *      play for an email whose customer was matched to that office, since a task
 *      needs an office and a customer.
 *   2. Reported by, per office: the ServiceTitan employee each office's tasks are
 *      reported by. One per office, no forwarding.
 *
 * Editing is admin-only. `can_edit` is the session's admin claim; it hides the
 * controls, but the API enforces the same rule (403), so this is courtesy, not
 * security.
 */

// Category names and colours are the Mailbox's (`./categories`), so a card here
// reads like the message card there; an unknown key falls back to the raw key.
const humanizeCategory = (key) => catLabel(key);

// The forward-to textarea takes one address per line or comma-separated; parse
// to a trimmed, de-duplicated list. The server cleans and validates too, so this
// only shapes the request.
export const parseForwardTo = (text) => {
  const out = [];
  for (const raw of (text || '').split(/[\n,]/)) {
    const addr = raw.trim();
    if (addr && !out.some((a) => a.toLowerCase() === addr.toLowerCase())) out.push(addr);
  }
  return out;
};

// The picked employee: the id, and the name and email the picker copied from
// the record (never typed). Empty becomes null.
const assigneeFields = (form) => {
  const name = form.name.trim();
  const email = form.email.trim();
  const idText = form.employee_id.trim();
  const employee_id = idText === '' ? null : Number.parseInt(idText, 10);
  return {
    employee_id: Number.isNaN(employee_id) ? null : employee_id,
    name: name === '' ? null : name,
    email: email === '' ? null : email,
  };
};

const EMPTY_FORM = { employee_id: '', name: '', email: '', forward_to: '' };

const formFromEntry = (entry) =>
  entry
    ? {
        employee_id: entry.employee_id != null ? String(entry.employee_id) : '',
        name: entry.name || '',
        email: entry.email || '',
        forward_to: (entry.forward_to || []).join('\n'),
      }
    : { ...EMPTY_FORM };

const hasAssignee = (entry) =>
  !!entry && (entry.employee_id != null || !!entry.email || !!entry.name);

const BTN = 'inline-flex items-center px-2.5 py-1 text-sm rounded-md';
const BTN_EDIT = `${BTN} text-gray-700 bg-gray-100 hover:bg-gray-200`;
const BTN_REMOVE = `${BTN} text-red-600 bg-red-50 hover:bg-red-100`;
const BTN_SAVE =
  'inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50';
const BTN_CANCEL =
  'inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50';
const INPUT = 'border border-gray-300 rounded-md px-2 py-1.5 text-sm';

/** The assignee half: the ServiceTitan employee the task goes to. */
const AssigneeView = ({ entry }) => {
  if (!hasAssignee(entry)) {
    return <span className="text-sm text-gray-400 italic">No task assignee</span>;
  }
  return (
    <div>
      <div className="text-sm text-gray-900">
        {entry.name || <span className="text-gray-400">(no name)</span>}
        {entry.email && <span className="text-gray-500"> · {entry.email}</span>}
        {entry.employee_id != null && (
          <span className="text-gray-400"> · #{entry.employee_id}</span>
        )}
      </div>
      {entry.employee_id == null && (
        // A row stored before 2026-10-19, when a contact could be entered by
        // email alone. The writer skips it; pick the employee to fix it.
        <span className="inline-flex items-center mt-1 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          Not a ServiceTitan employee: pick one
        </span>
      )}
    </div>
  );
};

/** Address chips. `muted` draws an inherited list, which this row did not set. */
const Addresses = ({ addrs, muted }) => (
  <div className="flex flex-wrap gap-1">
    {addrs.map((a) => (
      <span
        key={a}
        className={`inline-flex items-center text-sm rounded px-1.5 py-0.5 ${
          muted ? 'bg-gray-50 text-gray-400 border border-dashed border-gray-300' : 'bg-gray-100 text-gray-700'
        }`}
      >
        <Forward className={`h-3 w-3 mr-1 ${muted ? 'text-gray-300' : 'text-gray-400'}`} />
        {a}
      </span>
    ))}
  </div>
);

/**
 * The forward half of an office row: its own addresses, else the category's
 * (drawn muted and labelled), else nothing.
 */
const ForwardView = ({ entry, inherited }) => {
  const own = entry?.forward_to || [];
  if (own.length) return <Addresses addrs={own} />;
  if (inherited.length) {
    return (
      <div>
        <Addresses addrs={inherited} muted />
        <div className="mt-0.5 text-xs text-gray-400">Inherited from the category</div>
      </div>
    );
  }
  return <span className="text-sm text-gray-400 italic">No forwarding</span>;
};

/** A textarea for addresses, with the shared parsing rule in its placeholder. */
const ForwardTextarea = ({ value, onChange, placeholder }) => (
  <textarea
    rows={2}
    aria-label="Forward email to"
    placeholder={placeholder || 'email@example.com, one per line or comma-separated'}
    className={`w-full ${INPUT}`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const SaveCancel = ({ saving, onSave, onCancel }) => (
  <div className="flex gap-2 mt-2">
    <button type="button" disabled={saving} onClick={onSave} className={BTN_SAVE}>
      <Check className="h-4 w-4 mr-1" /> Save
    </button>
    <button type="button" disabled={saving} onClick={onCancel} className={BTN_CANCEL}>
      <X className="h-4 w-4 mr-1" /> Cancel
    </button>
  </div>
);

const EditRemove = ({ entry, onEdit, onRemove, editLabel }) => (
  <div className="flex gap-2 shrink-0 sm:ml-4">
    <button type="button" onClick={onEdit} className={BTN_EDIT} aria-label={editLabel}>
      {entry ? (
        <>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </>
      ) : (
        <>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </>
      )}
    </button>
    {entry && (
      <button
        type="button"
        onClick={onRemove}
        className={BTN_REMOVE}
        aria-label={`Remove ${editLabel}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

/**
 * One editable office line. A route (`withForward`) shows the assignee and the
 * forward list, with the category's list shown as inherited when the office has
 * none; the reporter shows only the person. The person is always picked from
 * the office's ServiceTitan employees: a task needs an employee id, so there is
 * no free entry (2026-10-19).
 */
const RoutingRow = ({
  label,
  entry,
  withForward,
  inherited = [],
  editing,
  canEdit,
  saving,
  form,
  onFormChange,
  employees,
  employeesLoading,
  onEdit,
  onCancel,
  onSave,
  onRemove,
}) => {
  // Picking an employee fills the id, name and email from that record, so the
  // stored contact is the ServiceTitan one; "none" clears all three.
  const onPickEmployee = (value) => {
    if (!value) {
      onFormChange({ ...form, employee_id: '', name: '', email: '' });
      return;
    }
    const emp = employees.find((e) => String(e.employee_id) === value);
    onFormChange({
      ...form,
      employee_id: value,
      name: emp?.name || form.name,
      email: emp?.email || form.email,
    });
  };

  const hasEmployeeList = employees.length > 0;
  // Keep the current id selectable even if it isn't in the fetched list (an
  // inactive employee, or a different tenant's cache mid-switch).
  const currentIdMissing =
    form.employee_id && !employees.some((e) => String(e.employee_id) === form.employee_id);

  if (editing) {
    return (
      <div className="py-3 border-b border-gray-100">
        {label && <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>}

        <div className="text-sm font-medium text-gray-500 mb-1">
          {withForward ? 'Assign ServiceTitan task to' : 'ServiceTitan employee'}
        </div>
        {hasEmployeeList ? (
          <select
            aria-label="Employee"
            className={`w-full ${INPUT} bg-white`}
            value={form.employee_id}
            onChange={(e) => onPickEmployee(e.target.value)}
          >
            <option value="">{withForward ? '— Nobody (forward only) —' : '— Pick one —'}</option>
            {currentIdMissing && (
              <option value={form.employee_id}>Employee #{form.employee_id} (not in list)</option>
            )}
            {employees.map((e) => (
              <option key={e.employee_id} value={String(e.employee_id)}>
                {e.name || `#${e.employee_id}`}
                {e.email ? ` — ${e.email}` : ''}
              </option>
            ))}
          </select>
        ) : (
          // No free entry: the list is the only way to name someone. Served by
          // the platform from the synced ServiceTitan employees.
          <div className="text-sm text-gray-500">
            {employeesLoading
              ? 'Loading this office\'s ServiceTitan employees…'
              : 'Could not load this office\'s ServiceTitan employees. Cancel and try again.'}
          </div>
        )}

        {withForward && (
          <>
            <div className="text-sm font-medium text-gray-500 mt-3 mb-1">
              Forward this office's email to
            </div>
            <ForwardTextarea
              value={form.forward_to}
              onChange={(v) => onFormChange({ ...form, forward_to: v })}
              placeholder={
                inherited.length
                  ? `Leave empty to use the category's list (${inherited.join(', ')})`
                  : undefined
              }
            />
          </>
        )}

        <p className="text-sm text-gray-500 mt-1">
          {withForward
            ? 'A ServiceTitan employee as assignee, a forward list, or both.'
            : 'The ServiceTitan employee this office\'s tasks are reported by.'}
        </p>
        <SaveCancel saving={saving} onSave={onSave} onCancel={onCancel} />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-2 py-3 border-b border-gray-100 sm:flex-row sm:items-start sm:justify-between ${
        entry ? '' : 'opacity-60'
      }`}
      data-set={entry ? 'true' : 'false'}
    >
      <div className="min-w-0 flex-1">
        {label && (
          <div className={`text-sm font-medium ${entry ? 'text-gray-900' : 'text-gray-600'}`}>
            {label}
          </div>
        )}
        {withForward ? (
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-400">Task assigned to</div>
              <div className="mt-0.5">
                <AssigneeView entry={entry} />
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-400">Forward to</div>
              <div className="mt-0.5">
                <ForwardView entry={entry} inherited={inherited} />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-0.5">
            {entry ? (
              <AssigneeView entry={entry} />
            ) : (
              <span className="text-sm text-gray-400 italic">Not set</span>
            )}
          </div>
        )}
      </div>
      {canEdit && (
        <EditRemove entry={entry} onEdit={onEdit} onRemove={onRemove} editLabel={label} />
      )}
    </div>
  );
};

/**
 * One category: its own forward list on the header line, and under it (when
 * opened) each office's assignee and forward override.
 */
const CategoryCard = ({
  category,
  forward,
  tenants,
  routeMap,
  open,
  onToggle,
  editingKey,
  canEdit,
  saving,
  form,
  onFormChange,
  employeesByTenant,
  onEditCategory,
  onSaveCategory,
  onRemoveCategory,
  onEditRoute,
  onSaveRoute,
  onRemoveRoute,
  onCancel,
}) => {
  const label = humanizeCategory(category);
  const inherited = forward?.forward_to || [];
  const editingCategory = editingKey === `cat:${category}`;
  // A category that does something stands out; one with nothing set sits back.
  const active =
    inherited.length > 0 || tenants.some((t) => routeMap.has(`${t.tenant_id}|${category}`));

  return (
    <div
      className={`rounded-lg border-l-4 ${
        active
          ? 'bg-white shadow-sm border border-gray-200'
          : 'bg-gray-50 border border-dashed border-gray-300'
      }`}
      style={{ borderLeftColor: catColor(category) }}
      data-active={active ? 'true' : 'false'}
    >
      <div className="flex flex-col gap-2 p-3 sm:p-4 sm:flex-row sm:items-start">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} offices for ${label}`}
          className={`flex items-center gap-1 text-left text-base font-medium sm:w-56 shrink-0 ${
            active ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          {label}
        </button>
        <div className="min-w-0 flex-1">
          {editingCategory ? (
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Forward email to</div>
              <ForwardTextarea
                value={form.forward_to}
                onChange={(v) => onFormChange({ ...form, forward_to: v })}
              />
              <p className="text-sm text-gray-500 mt-1">
                Every {label.toLowerCase()} email, whether or not its customer was matched,
                unless the customer's office has its own list below.
              </p>
              <SaveCancel saving={saving} onSave={onSaveCategory} onCancel={onCancel} />
            </div>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-sm uppercase tracking-wide text-gray-400">Forward to</div>
                <div className="mt-0.5">
                  {inherited.length ? (
                    <Addresses addrs={inherited} />
                  ) : (
                    <span className="text-sm text-gray-400 italic">No forwarding</span>
                  )}
                </div>
              </div>
              {canEdit && (
                <EditRemove
                  entry={forward}
                  onEdit={onEditCategory}
                  onRemove={onRemoveCategory}
                  editLabel={`${label} forwarding`}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-3 sm:px-4 pb-1">
          <p className="pt-2 text-sm text-gray-500">
            Applies only when the customer was matched to the office. An office's own forward
            list overrides the category's.
          </p>
          {tenants.map((t) => {
            const key = `route:${t.tenant_id}|${category}`;
            const entry = routeMap.get(`${t.tenant_id}|${category}`);
            const emp = employeesByTenant[t.tenant_id];
            return (
              <RoutingRow
                key={t.tenant_id}
                label={t.location_code ? `${t.name} (${t.location_code})` : t.name}
                entry={entry}
                withForward
                inherited={inherited}
                editing={editingKey === key}
                canEdit={canEdit}
                saving={saving}
                form={form}
                onFormChange={onFormChange}
                employees={emp?.list || []}
                employeesLoading={!!emp?.loading}
                onEdit={() => onEditRoute(t.tenant_id, key, entry)}
                onCancel={onCancel}
                onSave={() => onSaveRoute(t.tenant_id)}
                onRemove={() => onRemoveRoute(t.tenant_id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaskRouting = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which categories are opened to their offices.
  const [openCategories, setOpenCategories] = useState(() => new Set());
  // Which row is in edit mode: `cat:<category>`, `route:<tenant>|<category>` or
  // `reporter:<tenant>`. Only one at a time, so a stray draft cannot be saved
  // against the wrong row.
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // ServiceTitan employees for the assignee picker, cached per tenant:
  // { [tenantId]: { loading, list, error } }. Employee ids are per tenant, so
  // each tenant is fetched on its own, when a row of its is first edited.
  const [employeesByTenant, setEmployeesByTenant] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const data = await getRoutingOverview();
      setOverview(data);
      setError(null);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load task routing.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canEdit = !!overview?.can_edit;

  // Tenants whose employees we've already started fetching, so a tenant is
  // requested exactly once. A ref, not state, on purpose: the fetch must not
  // depend on the map it writes to.
  const requestedTenants = useRef(new Set());

  // Served by the platform (silver.st_employees), not the service inbox. If it
  // fails the row falls back to manual id entry, so a failure is cached quietly
  // rather than toasted.
  const fetchEmployees = (tenantId) => {
    if (requestedTenants.current.has(tenantId)) return;
    requestedTenants.current.add(tenantId);
    setEmployeesByTenant((prev) => ({
      ...prev,
      [tenantId]: { loading: true, list: [], error: null },
    }));
    axios
      .get('/api/servicetitan/employees', { params: { tenant_id: tenantId } })
      .then(({ data }) => {
        setEmployeesByTenant((prev) => ({
          ...prev,
          [tenantId]: { loading: false, list: data || [], error: null },
        }));
      })
      .catch((err) => {
        // Allow a retry the next time a row of this tenant is edited.
        requestedTenants.current.delete(tenantId);
        setEmployeesByTenant((prev) => ({
          ...prev,
          [tenantId]: { loading: false, list: [], error: err },
        }));
      });
  };

  const routeMap = useMemo(() => {
    const m = new Map();
    for (const r of overview?.routes || []) m.set(`${r.tenant_id}|${r.category}`, r);
    return m;
  }, [overview]);

  const reporterMap = useMemo(() => {
    const m = new Map();
    for (const r of overview?.reporters || []) m.set(r.tenant_id, r);
    return m;
  }, [overview]);

  const forwardMap = useMemo(() => {
    const m = new Map();
    for (const f of overview?.category_forwards || []) m.set(f.category, f);
    return m;
  }, [overview]);

  const toggleCategory = (category) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const startEdit = (key, entry) => {
    setEditingKey(key);
    setForm(formFromEntry(entry));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setForm(EMPTY_FORM);
  };

  // Every write refetches the whole overview: it is a handful of rows, and the
  // response is what carries the fresh updated_by / updated_at back.
  const write = async (call) => {
    try {
      setSaving(true);
      await call();
      await load();
      cancelEdit();
      toast.success('Saved.');
    } catch (err) {
      toast.error(apiMessage(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  // A 404 on remove means it was already gone — reload and move on.
  const remove = async (call) => {
    try {
      await call();
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(apiMessage(err, 'Could not remove.'));
        return;
      }
    }
    await load();
  };

  const saveCategory = (category) => {
    const forward_to = parseForwardTo(form.forward_to);
    if (forward_to.length === 0) {
      toast.error('Enter at least one forward-to address.');
      return;
    }
    return write(() => putCategoryForward(category, forward_to));
  };

  const saveRoute = (tenantId, category) => {
    const assignee = assigneeFields(form);
    const forward_to = parseForwardTo(form.forward_to);
    if (assignee.employee_id == null && forward_to.length === 0) {
      toast.error('Pick a ServiceTitan employee or enter a forward-to address.');
      return;
    }
    return write(() => putRoute(tenantId, category, { ...assignee, forward_to }));
  };

  const saveReporter = (tenantId) => {
    const person = assigneeFields(form);
    if (person.employee_id == null) {
      toast.error('Pick a ServiceTitan employee.');
      return;
    }
    return write(() => putReporter(tenantId, person));
  };

  if (loading) {
    return (
      <div className="flex items-center text-gray-500">
        <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Loading task routing…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4">{error}</div>
      </div>
    );
  }

  const tenants = overview.tenants || [];
  const categories = overview.categories || [];

  return (
    <div className="max-w-4xl cat-palette">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Task Routing</h1>
        <p className="mt-1 text-sm text-gray-600">
          Where each category's email is forwarded, and who its ServiceTitan task goes to.
          {canEdit ? '' : ' You can view routing; only an administrator can change it.'}
        </p>
      </div>

      <div className="mb-5 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
        <ul className="list-disc pl-4 space-y-1">
          <li>
            Each category has a <strong>forward list</strong>. Every email in the category is
            forwarded there, whether or not its customer was found.
          </li>
          <li>
            Open a category to set, per office, who its <strong>ServiceTitan task</strong> is
            assigned to and, if that office wants different people, its own forward list. An
            office with no list of its own inherits the category's. Office settings apply to an
            email whose customer was matched to that office: a task needs a customer.
          </li>
          <li>
            Assignees and reporters are picked from the office's{' '}
            <strong>ServiceTitan employees</strong>: a task is created against an employee id,
            so there is no free entry.
          </li>
        </ul>
      </div>

      <div className="mb-2">
        <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          By category
        </div>
      </div>
      <div className="space-y-3">
        {categories.map((category) => (
          <CategoryCard
            key={category}
            category={category}
            forward={forwardMap.get(category)}
            tenants={tenants}
            routeMap={routeMap}
            open={openCategories.has(category)}
            onToggle={() => toggleCategory(category)}
            editingKey={editingKey}
            canEdit={canEdit}
            saving={saving}
            form={form}
            onFormChange={setForm}
            employeesByTenant={employeesByTenant}
            onEditCategory={() => startEdit(`cat:${category}`, forwardMap.get(category))}
            onSaveCategory={() => saveCategory(category)}
            onRemoveCategory={() => remove(() => deleteCategoryForward(category))}
            onEditRoute={(tenantId, key, entry) => {
              fetchEmployees(tenantId);
              startEdit(key, entry);
            }}
            onSaveRoute={(tenantId) => saveRoute(tenantId, category)}
            onRemoveRoute={(tenantId) => remove(() => deleteRoute(tenantId, category))}
            onCancel={cancelEdit}
          />
        ))}
      </div>

      <div className="mt-8 mb-2">
        <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Reported by, per office
        </div>
        <p className="text-sm text-gray-500 mt-0.5">
          The person each office's tasks are reported by — who ServiceTitan shows as having
          created it (not who it is assigned to). One per office; a task needs one.
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-5">
        {tenants.map((t) => {
          const key = `reporter:${t.tenant_id}`;
          const entry = reporterMap.get(t.tenant_id);
          const emp = employeesByTenant[t.tenant_id];
          return (
            <RoutingRow
              key={t.tenant_id}
              label={t.location_code ? `${t.name} (${t.location_code})` : t.name}
              entry={entry}
              withForward={false}
              editing={editingKey === key}
              canEdit={canEdit}
              saving={saving}
              form={form}
              onFormChange={setForm}
              employees={emp?.list || []}
              employeesLoading={!!emp?.loading}
              onEdit={() => {
                fetchEmployees(t.tenant_id);
                startEdit(key, entry);
              }}
              onCancel={cancelEdit}
              onSave={() => saveReporter(t.tenant_id)}
              onRemove={() => remove(() => deleteReporter(t.tenant_id))}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TaskRouting;
